import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { getToken } from "../lib/auth";
import {
  LayoutDashboard, Package, History, Bell, LogOut, Menu, X,
  ChevronRight, ArrowLeft, Truck
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0d053155-ce00-4580-8f42-e450395a24b2/artifacts/1forsbvk_logo%20cmes.png";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

const courierNavItems = [
  { path: "/coursier", icon: LayoutDashboard, label: "Tableau de bord" },
  { path: "/coursier/courses", icon: Package, label: "Mes courses" },
  { path: "/coursier/historique", icon: History, label: "Historique" },
  { path: "/coursier/notifications", icon: Bell, label: "Notifications" },
];

export default function CourierLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = useCallback(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/log/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUnreadCount(r.data.unread || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)' }}>
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex flex-col w-60 backdrop-blur-xl border-r border-slate-200/50 fixed h-full z-50" style={{ backgroundColor: '#0f172a' }}>
        <div className="h-16 flex items-center px-5 border-b border-white/5">
          <Link to="/hub" className="flex items-center gap-2.5 group" data-testid="courier-back-hub">
            <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
            <img src={LOGO_URL} alt="CMES" className="h-8 w-auto" />
            <div>
              <span className="font-bold text-emerald-400 text-sm tracking-tight">CMES</span>
              <span className="block text-[9px] text-slate-500 tracking-widest font-semibold">COURSIER</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {courierNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`courier-nav-${item.path.split("/").pop() || "dashboard"}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-emerald-500/15 text-emerald-400 font-semibold border-l-2 border-emerald-500"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          {/* Notification link */}
          <Link to="/coursier/notifications" data-testid="courier-notif-bell"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all mb-2 text-slate-400 hover:bg-white/5 hover:text-slate-200">
            <div className="relative">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </div>
            <span>Notifications</span>
            {unreadCount > 0 && <span className="ml-auto text-[10px] text-red-400 font-medium">{unreadCount}</span>}
          </Link>
          <div className="flex items-center gap-2.5">
            <Avatar className="h-8 w-8 border border-white/10">
              <AvatarFallback className="bg-emerald-500/20 text-emerald-400 text-xs font-semibold">
                {user?.full_name?.charAt(0) || "C"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-300 truncate">{user?.full_name}</p>
              <p className="text-[10px] text-slate-500 truncate">Agent Commercial</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="courier-logout-btn" className="text-slate-500 hover:text-red-400 h-7 w-7">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 backdrop-blur-md border-b border-slate-200/50 z-50 flex items-center justify-between px-4" style={{ backgroundColor: '#0f172a' }}>
        <div className="flex items-center gap-2">
          <Link to="/hub" className="text-slate-400 hover:text-emerald-400"><ArrowLeft className="w-4 h-4" /></Link>
          <Truck className="w-4 h-4 text-emerald-400" />
          <span className="font-bold text-emerald-400 text-sm">COURSIER</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/coursier/notifications" className="relative text-slate-400">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 backdrop-blur-md pt-14" style={{ backgroundColor: 'rgba(15,23,42,0.98)' }}>
          <nav className="p-4 space-y-1">
            {courierNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${isActive ? "bg-emerald-500/15 text-emerald-400 font-semibold" : "text-slate-400 hover:bg-white/5"}`}>
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  <ChevronRight className="w-4 h-4 ml-auto opacity-40" />
                </Link>
              );
            })}
          </nav>
          <div className="p-4 mt-4 border-t border-white/5">
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-red-400 px-4 py-2">
              <LogOut className="w-4 h-4" /> Deconnexion
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-60 pt-14 md:pt-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          {(title || subtitle) && (
            <div className="mb-6">
              {title && <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">{title}</h1>}
              {subtitle && <p className="text-sm mt-1 text-slate-500">{subtitle}</p>}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
